const API_URL = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:8010';

export interface AIConfig {
  GROQ_API_KEY?: { value: string; type: string; description?: string };
  DEEPSEEK_API_KEY?: { value: string; type: string; description?: string };
  SAMBANOVA_API_KEY?: { value: string; type: string; description?: string };
  LLM_PROVIDER?: { value: string; type: string; description?: string };
}

export interface Prompt {
  id: string;
  agent: string;
  version: string;
  content: string;
  description?: string;
  is_active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PromptListItem {
  id: string;
  agent: string;
  version: string;
  description?: string;
  is_active: boolean;
  createdAt: string;
  updatedAt: string;
}

export class AIConfigService {
  private static getAuthHeaders(token: string): HeadersInit {
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': 'true',
    };
  }

  /**
   * Obtener configuraciones de IA
   */
  static async getAIConfig(token: string): Promise<AIConfig> {
    const response = await fetch(`${API_URL}/api/admin/ai-config`, {
      headers: this.getAuthHeaders(token),
    });

    if (!response.ok) {
      throw new Error('Error obteniendo configuración de IA');
    }

    return await response.json();
  }

  /**
   * Actualizar configuraciones de IA
   */
  static async updateAIConfig(token: string, config: Partial<AIConfig>): Promise<void> {
    const response = await fetch(`${API_URL}/api/admin/ai-config`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(token),
      body: JSON.stringify(config),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Error actualizando configuración de IA');
    }
  }

  /**
   * Listar prompts
   */
  static async getPrompts(
    token: string,
    page: number = 1,
    limit: number = 50,
    agent?: string,
    isActive?: boolean
  ): Promise<{ prompts: PromptListItem[]; pagination: any }> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (agent) params.append('agent', agent);
    if (isActive !== undefined) params.append('is_active', isActive.toString());

    const response = await fetch(`${API_URL}/api/admin/prompts?${params}`, {
      headers: this.getAuthHeaders(token),
    });

    if (!response.ok) {
      throw new Error('Error obteniendo prompts');
    }

    return await response.json();
  }

  /**
   * Obtener prompt por ID
   */
  static async getPrompt(token: string, id: string): Promise<Prompt> {
    const response = await fetch(`${API_URL}/api/admin/prompts/${id}`, {
      headers: this.getAuthHeaders(token),
    });

    if (!response.ok) {
      throw new Error('Error obteniendo prompt');
    }

    return await response.json();
  }

  /**
   * Obtener prompt por agent y version
   */
  static async getPromptByAgentVersion(
    token: string,
    agent: string,
    version: string
  ): Promise<Prompt> {
    const response = await fetch(
      `${API_URL}/api/admin/prompts/agent/${agent}/version/${version}`,
      {
        headers: this.getAuthHeaders(token),
      }
    );

    if (!response.ok) {
      throw new Error('Error obteniendo prompt');
    }

    return await response.json();
  }

  /**
   * Obtener versiones de un agent
   */
  static async getPromptVersions(token: string, agent: string): Promise<{ agent: string; versions: PromptListItem[] }> {
    const response = await fetch(`${API_URL}/api/admin/prompts/agent/${agent}/versions`, {
      headers: this.getAuthHeaders(token),
    });

    if (!response.ok) {
      throw new Error('Error obteniendo versiones');
    }

    return await response.json();
  }

  /**
   * Crear nuevo prompt
   */
  static async createPrompt(token: string, prompt: {
    agent: string;
    version: string;
    content: string;
    description?: string;
    is_active?: boolean;
  }): Promise<Prompt> {
    const response = await fetch(`${API_URL}/api/admin/prompts`, {
      method: 'POST',
      headers: this.getAuthHeaders(token),
      body: JSON.stringify(prompt),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Error creando prompt');
    }

    return await response.json();
  }

  /**
   * Actualizar prompt
   */
  static async updatePrompt(
    token: string,
    id: string,
    updates: {
      agent?: string;
      version?: string;
      content?: string;
      description?: string;
      is_active?: boolean;
    }
  ): Promise<Prompt> {
    const response = await fetch(`${API_URL}/api/admin/prompts/${id}`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(token),
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Error actualizando prompt');
    }

    return await response.json();
  }

  /**
   * Eliminar prompt (desactivar)
   */
  static async deletePrompt(token: string, id: string): Promise<void> {
    const response = await fetch(`${API_URL}/api/admin/prompts/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(token),
    });

    if (!response.ok) {
      throw new Error('Error eliminando prompt');
    }
  }
}

